import type { CurrentRead } from '@signalvote/data';
import type { ComplianceReportDto, CurrentClosePeriodDto } from '@signalvote/shared';
import { useCurrentClosePeriod } from './useCloseHub';
import { useFinanceReports } from './useFinanceReports';
import { useFinanceHealth } from './useFinanceDeadlines';

export interface CurrentPeriodReport {
  /** The reporting period the File flow acts on (the owed report's period, or
   *  the calendar-open period when caught up), or `null` when none applies. */
  period: CurrentClosePeriodDto | null;
  /** The report belonging to that period, or `null` when none exists yet. */
  report: ComplianceReportDto | null;
  /** Convenience id for `useFilingPreflight` / detail; `null` until a report exists. */
  reportId: string | null;
  /** True once a report has been generated for the resolved period. */
  hasReport: boolean;
}

/**
 * AFU-006 (Task 3.2) — the period→report resolution extracted from
 * `CloseFileRoute` so the close hub's filing-readiness surface
 * (`CloseHubPreflight`) resolves the SAME report the File flow does, instead
 * of re-deriving it. Single source of truth for "which report does this close
 * page file?": match the row whose `deadlineId` is the resolved period, else
 * fall back to the most-recent row (the list endpoint sorts by recency and is
 * already filtered by `deadlineId`, so the fallback is the amendment chain's
 * latest — same rule `CloseFileRoute` used inline).
 *
 * Period resolution prefers the OWED report (`health.reportToActOn`, the earliest
 * unfiled report that carries the treasurer's entered activity) over the
 * calendar-open period (`GET /close/current`, the period containing today). In the
 * gap window — June's period has ended but its deadline hasn't passed, so July is
 * the calendar-open period — the File flow MUST file June, matching the close
 * page's June-scoped display + readiness gate ({@link useOwedReportWindow}).
 * Without this, the gate says "file June" while the File modal would open and
 * generate an empty July report. When caught up (no owed report, or it carries no
 * period bounds) the calendar-open period stands. This is the filing-action
 * counterpart of the display re-scope: gate and action share one owed source.
 *
 * A failed, offline or loading health read passes through as itself: the owed report is unknown,
 * so the calendar-open period must not stand in for it. The read is `loading` until the period
 * and (once resolved) its report list have answered.
 */
export function useCurrentPeriodReport(): CurrentRead<CurrentPeriodReport> {
  const { data: openPeriod, isLoading: periodLoading } = useCurrentClosePeriod();
  const health = useFinanceHealth();

  const owed = health.status === 'ready' ? health.data.reportToActOn : null;
  const period: CurrentClosePeriodDto | null =
    owed && owed.periodStart && owed.periodEnd
      ? {
          id: owed.id,
          name: owed.name,
          periodStart: owed.periodStart,
          periodEnd: owed.periodEnd,
          dueDate: owed.dueDate,
          // The server resolves both from the same owed-period selector, so when the
          // ids match the open period's flag is this report's.
          isOverdue: openPeriod?.id === owed.id && openPeriod.isOverdue,
          // This synthesized period only carries the owed report's id/window for
          // report resolution — the duplicate-flag count is not sourced here (it
          // rides the /close/current response, read directly by CloseMasthead).
          openDuplicateFlagCount: 0,
        }
      : (openPeriod ?? null);

  const deadlineId = period?.id;
  const { data: reportsList, isLoading: reportsLoading } = useFinanceReports(
    deadlineId ? { deadlineId, pageSize: 5 } : { pageSize: 0 },
  );

  const reports = reportsList?.items ?? [];
  const report =
    reports.find((r) => r.deadlineId === deadlineId) ?? reports[0] ?? null;

  if (health.status !== 'ready') return health;
  if (periodLoading || (!!period && reportsLoading)) return { status: 'loading' };
  return {
    status: 'ready',
    data: { period, report, reportId: report?.id ?? null, hasReport: report != null },
    refreshing: health.refreshing,
  };
}
